/**
 * How money works here, in four lines (L60 of docs/plans/ledger.md, Phase 13):
 * the one set of words the setup studio's card before Build, the Dashboard's
 * card after it, and OS's answer to "how does money work here?" all say, so
 * the three can never tell a business two stories.
 *
 *   1  what it was for   every money entry says what the money was for, in
 *                        the records that move money (L3, L16)
 *   2  both sides        RecordOS counts both sides of each entry, so Cash,
 *                        what people owe and profit are worked out, never
 *                        typed; and when income counts, by the patterns the
 *                        records use (L24)
 *   3  To sort           Not sure waits in To sort (L17)
 *   4  what OS set       the financial year (L42), the tax (L43) and the
 *                        approvals (L55), as the setup set them
 *
 * The words explain the idea, never the code names: no line says ledger,
 * posting or debit (L19), and every word is a catalogue key.
 *
 * The facts come from a draft (`moneyFactsOfDraft`, the studio) or from a
 * built workspace (the server's read of its rules, settings and chains); the
 * words come from the facts alone (`explainMoney`). Pure but for `t`.
 */
import type { ApprovalConditions } from './api';
import { fiscalYearLine } from './fiscal-year';
import { formatMoney } from './format';
import { t } from './i18n/t';
import type { PostingPattern } from './ledger/patterns';
import { buildFiscalYearStart, type SetupMoneyDecision } from './money-setup';
import type { StudioDraft, StudioRecord } from './setup-draft/draft';
import type { FiscalYearStart, TaxRegime } from './tax-countries';

/** When income counts (L24): when the money comes in, when a sale or a charge is made, or both, by record. */
export type MoneyBasis = 'cash' | 'earned' | 'both';

/** A record whose entries wait for approval before they count (L55): every entry, or those above an amount. */
export type MoneyApprovalFact = { readonly record: string; readonly above: number | null };

/** What the four lines are made from. */
export type MoneyExplainerFacts = {
  /** The records whose entries say what the money was for, in the order the person sees them. */
  readonly records: readonly string[];
  /** When income counts; null when no record earns or takes money in. */
  readonly basis: MoneyBasis | null;
  /** The list whose rows owe the business (Customers, Students); null says customers. */
  readonly owedBy: string | null;
  /** The financial year's start; null when it is not set yet. */
  readonly fiscalYearStart: FiscalYearStart | null;
  /** How tax is kept; null when nobody has said yet. */
  readonly taxRegime: TaxRegime | null;
  readonly approvals: readonly MoneyApprovalFact[];
  /** The workspace's currency, for an approval's amount. */
  readonly currency: string | null;
};

export type MoneyExplainer = {
  readonly title: string;
  readonly lines: readonly [string, string, string, string];
};

/* ------------------------------------------------------------- the facts */

/** Patterns that count income when the money comes in. */
const CASH_PATTERNS: ReadonlySet<PostingPattern> = new Set(['money_in', 'money_out']);
/** Patterns that count income when a sale, a bill or a charge is made, and clear it with a payment. */
const EARNED_PATTERNS: ReadonlySet<PostingPattern> = new Set(['sale', 'purchase', 'charge', 'bill_lines']);
/**
 * Patterns whose entries say no "what was it for": they move money between
 * accounts the person picked, or (quantity) move no money at all.
 */
const NO_PURPOSE: ReadonlySet<PostingPattern> = new Set(['adjustment', 'opening_balance', 'tax_payment', 'charge_plan', 'transfer', 'quantity']);

/** Whether a rule that may post by these patterns moves money: a rule that moves only quantity does not (L59). */
export const patternsMoveMoney = (patterns: readonly PostingPattern[]): boolean => patterns.some((one) => one !== 'quantity');

/** Whether a draft has a record that moves money, so the card has something to say. */
export const draftMovesMoney = (draft: StudioDraft): boolean =>
  draft.records.some((one) => one.kind === 'record' && one.posting !== undefined && patternsMoveMoney(patternsOf(one)));
/** Patterns whose party owes the business, the first one found naming who owes; a charge first. */
const OWED_PATTERNS: readonly PostingPattern[] = ['charge', 'sale', 'receipt', 'money_in'];

/** Every pattern a record's rule may post by: its own and each both-ways option's (L44). */
const patternsOf = (record: StudioRecord): PostingPattern[] => {
  const rule = record.posting;
  if (rule === undefined) return [];
  const options = Object.values(rule.byChoice?.options ?? {}).flatMap((one) => (one.pattern === undefined ? [] : [one.pattern]));
  return [rule.pattern, ...options];
};

/** When income counts, from the patterns the money records post by. */
export const moneyBasisOf = (patterns: readonly PostingPattern[]): MoneyBasis | null => {
  const cash = patterns.some((one) => CASH_PATTERNS.has(one));
  const earned = patterns.some((one) => EARNED_PATTERNS.has(one));
  if (cash && earned) return 'both';
  if (earned) return 'earned';
  if (cash) return 'cash';
  return null;
};

/** The list a record's party role picks from, by name. */
const partyListOf = (draft: StudioDraft, record: StudioRecord): string | null => {
  const source = record.posting?.roles?.party;
  if (source === undefined || !('fieldKey' in source)) return null;
  const field = record.fields.find((one) => one.key === source.fieldKey);
  const target = field?.type === 'reference' ? field.targetRecordKey : undefined;
  const list = target === undefined ? undefined : draft.records.find((one) => one.key === target && one.kind === 'directory');
  return list?.name ?? null;
};

/** One record that moves money: its name, every pattern its rule may post by, and the list its party picks from. */
export type MoneyRecordFact = { readonly name: string; readonly patterns: readonly PostingPattern[]; readonly owedBy: string | null };

/** What the setup or the workspace set, beside its records. */
export type MoneySetFacts = {
  readonly fiscalYearStart: FiscalYearStart | null;
  readonly taxRegime: TaxRegime | null;
  readonly approvals: readonly MoneyApprovalFact[];
  readonly currency: string | null;
};

/**
 * The facts of any set of money records, in the order the person sees them:
 * a draft's (`moneyFactsOfDraft`) and a built workspace's (the server's read
 * of its live rules) both come through here, so the two cannot drift.
 */
export const moneyFactsOf = (records: readonly MoneyRecordFact[], set: MoneySetFacts): MoneyExplainerFacts => ({
  records: records.filter((one) => !one.patterns.every((pattern) => NO_PURPOSE.has(pattern))).map((one) => one.name),
  basis: moneyBasisOf(records.flatMap((one) => one.patterns)),
  owedBy: OWED_PATTERNS.flatMap((pattern) => records.filter((one) => one.patterns.includes(pattern)).flatMap((one) => one.owedBy ?? []))[0] ?? null,
  ...set,
});

/** An approval chain as the four lines say it: every entry, or those above an amount. The server reads a live chain the same way. */
export const approvalFactOf = (record: string, conditions: ApprovalConditions): MoneyApprovalFact => ({
  record,
  above: conditions.every !== true && typeof conditions.above === 'number' ? conditions.above : null,
});

/**
 * A draft's facts (the studio's card before Build, 13.1): its money records,
 * its approvals, and the year and tax OS set from the conversation, which
 * the caller reads (`decideSetupMoney`), as Build will write them. The
 * approvals are the chains the setup wrote from words (L55), which Build
 * saves on each record, and the Adjustment record's one-step chain (L18)
 * where the setup wrote it none: every adjustment waits, but for the
 * owner's and an admin's own.
 */
export const moneyFactsOfDraft = (
  draft: StudioDraft,
  money: { readonly fiscalYearStart: FiscalYearStart | null; readonly taxRegime: TaxRegime | null },
): MoneyExplainerFacts => {
  const moving = draft.records.filter((one) => one.kind === 'record' && one.posting !== undefined);
  return moneyFactsOf(
    moving.map((one) => ({ name: one.name, patterns: patternsOf(one), owedBy: partyListOf(draft, one) })),
    {
      fiscalYearStart: money.fiscalYearStart,
      taxRegime: money.taxRegime,
      approvals: draft.records.flatMap((one) => {
        if (one.kind !== 'record') return [];
        if (one.approvalChain !== undefined) return [approvalFactOf(one.name, one.approvalChain.conditions)];
        return one.posting?.pattern === 'adjustment' ? [{ record: one.name, above: null }] : [];
      }),
      currency: draft.meta.currency,
    },
  );
};

/* ------------------------------------------------------------- the words */

/** "A", "A and B", "A, B and C". */
const listWords = (items: readonly string[]): string => {
  if (items.length <= 1) return items[0] ?? '';
  return t('money.explain.list', { first: items.slice(0, -1).join(', '), last: items[items.length - 1]! });
};

const basisWords = (basis: MoneyBasis | null): string | null => {
  if (basis === null) return null;
  if (basis === 'cash') return t('money.explain.basis.cash');
  if (basis === 'earned') return t('money.explain.basis.earned');
  return t('money.explain.basis.both');
};

const taxWords = (regime: TaxRegime | null): string => {
  if (regime === null) return t('money.explain.tax.unset');
  if (regime === 'in_gst') return t('money.explain.tax.in_gst');
  if (regime === 'single') return t('money.explain.tax.single');
  if (regime === 'sales_tax') return t('money.explain.tax.sales_tax');
  return t('money.explain.tax.none');
};

const approvalWords = (approvals: readonly MoneyApprovalFact[], currency: string | null): string => {
  if (approvals.length === 0) return t('money.explain.approvals.none');
  const items = approvals.map((one) =>
    one.above === null ? t('money.explain.approvals.every', { record: one.record }) : t('money.explain.approvals.above', { record: one.record, amount: formatMoney(one.above, currency) }),
  );
  return t('money.explain.approvals.some', { list: listWords(items) });
};

/** The four lines, from the facts alone: the studio, the Dashboard and OS say these words. */
export const explainMoney = (facts: MoneyExplainerFacts): MoneyExplainer => {
  const purpose = facts.records.length === 0 ? t('money.explain.what_for_none') : t('money.explain.what_for', { records: listWords(facts.records) });
  const who = facts.owedBy === null || facts.owedBy.trim() === '' ? t('money.explain.customers') : facts.owedBy;
  const sides = [t('money.explain.both_sides', { who }), basisWords(facts.basis)].filter((one): one is string => one !== null).join(' ');
  const year = facts.fiscalYearStart === null ? t('money.explain.year_unset') : fiscalYearLine(facts.fiscalYearStart);
  const set = [year, taxWords(facts.taxRegime), approvalWords(facts.approvals, facts.currency)].join(' ');
  return { title: t('money.explain.title'), lines: [purpose, sides, t('money.explain.to_sort'), set] };
};

/**
 * A setup's four lines, from its draft and what OS decided about money
 * (`decideSetupMoney`): the year Build will write, and the tax once it is
 * set (a question nobody answered says it is not set yet). The studio's card
 * and OS in setup (`note_money`) both say these.
 */
export const explainSetupMoney = (draft: StudioDraft, decision: SetupMoneyDecision): MoneyExplainer =>
  explainMoney(
    moneyFactsOfDraft(draft, {
      fiscalYearStart: buildFiscalYearStart(decision),
      taxRegime: decision.tax.kind === 'set' ? decision.tax.regime : null,
    }),
  );

/** The four lines as one answer, a line each: what OS says when asked "how does money work here?" (13.3). */
export const moneyExplainerText = (explainer: MoneyExplainer): string => [explainer.title, ...explainer.lines].join('\n');
